
import { useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Article } from '@/types/article-types';

export const useArticleDataFetching = (
  isEditMode: boolean,
  id: string | undefined,
  fetchArticleById: (id: string) => Promise<Article | null>,
  setFormValues: {
    setTitle: (title: string) => void;
    setLeadText: (leadText: string) => void;
    setContent: (content: string) => void;
    setHeaderImageUrl: (url: string) => void;
    setAuthorId: (id: string) => void;
    setSelectedTags: (tags: string[]) => void;
    setStatus: (status: any) => void;
    setPublishDate: (date: Date | undefined) => void;
  },
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
) => {
  const { toast } = useToast();

  useEffect(() => {
    const fetchArticleData = async () => {
      if (isEditMode && id) {
        try {
          const article = await fetchArticleById(id);
          
          if (article) {
            setFormValues.setTitle(article.title || '');
            setFormValues.setLeadText(article.lead_text || '');
            setFormValues.setContent(article.content || '');
            setFormValues.setHeaderImageUrl(article.header_image_url || '');
            setFormValues.setAuthorId(article.author_id || '');
            
            // Handle tags - extract only the tag IDs as strings
            if (article.tags && Array.isArray(article.tags)) {
              // Properly extract tag IDs ensuring they're all strings
              const tagIds = article.tags.map(tag => {
                if (typeof tag === 'string') {
                  return tag;
                } else if (typeof tag === 'object' && tag !== null && 'id' in tag) {
                  return tag.id;
                }
                return null;
              }).filter((id): id is string => id !== null);
              
              setFormValues.setSelectedTags(tagIds);
            }
            
            setFormValues.setStatus(article.status || 'draft');
            
            // Convert publish_date string to Date object if it exists
            if (article.publish_date) {
              setFormValues.setPublishDate(new Date(article.publish_date));
            }
          } else {
            setError('Article not found');
            toast({
              variant: "destructive",
              title: "Error",
              description: "Could not find the article you're trying to edit"
            });
          }
        } catch (error: any) {
          console.error('Error fetching article:', error);
          setError(error.message || 'Failed to load article');
          toast({
            variant: "destructive",
            title: "Error",
            description: error.message || "Failed to load article data"
          });
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchArticleData();
  }, [isEditMode, id, fetchArticleById, setFormValues, setLoading, setError, toast]);
};
